import { useCallback } from 'react';

import { useRequestAuthentication } from '@/packages/auth/useRequestAuthentication';
import { useAddComparisonMutation } from '@/packages/query-state/shared/store/сomparison/v1/add-to-comparison/mutation';
import { useDeleteFromComparisonMutation } from '@/packages/query-state/shared/store/сomparison/v1/delete-from-comparison/mutation';
import { useReduxDispatch } from '@/packages/redux-state/store';
import { comparisonNotificationActions } from '@/packages/redux-state/store/slices/comparisonNotification';
import { useUserIsAuthenticatedSelector } from '@/packages/redux-state/store/slices/user';

import { useCardApiContext } from '../../context/CardApiContext';
import { useCardContext } from '../../context/CardContext';
import { useListingAppContext } from '../../context/ListingAppContext';
import { cardOfferDataSelector, cardOfferSelector } from '../../context/selectors';

import { useCAForCard } from './useCAForCard';

export function useCompareOfferAction(): VoidFunction {
  const cardContext = useCardContext();
  const cardApi = useCardApiContext();
  const appContext = useListingAppContext();
  const dispatch = useReduxDispatch();

  const { comparisonStatus } = cardOfferDataSelector(cardContext);
  const { id: offerId } = cardOfferSelector(cardContext);
  const isUserAuthenticated = useUserIsAuthenticatedSelector();
  const requestAuthentication = useRequestAuthentication(appContext);

  const isAdded = comparisonStatus ? comparisonStatus.status === 'added' : false;

  const { mutate: addToComparison } = useAddComparisonMutation(cardApi.addToComparisonMutationOptions);
  const { mutate: deleteFromComparison } = useDeleteFromComparisonMutation(cardApi.deleteFromComparisonMutationOptions);

  const setComparisonNotificationType = useCallback(
    (type: string) =>
      dispatch(comparisonNotificationActions.setComparisonNotificationType({ lastOfferId: offerId, type })),
    [dispatch, offerId],
  );

  const caForCard = useCAForCard();

  return useCallback<VoidFunction>(() => {
    if (isUserAuthenticated) {
      caForCard({ action: isAdded ? 'Remove' : 'Add', category: 'Compare' }, { label: true });

      if (isAdded) {
        deleteFromComparison({ offerId });
        setComparisonNotificationType('delete');
      } else {
        addToComparison({ offerId });
        setComparisonNotificationType('add');
      }
    } else {
      requestAuthentication();
    }
  }, [
    isUserAuthenticated,
    caForCard,
    isAdded,
    deleteFromComparison,
    offerId,
    setComparisonNotificationType,
    addToComparison,
    requestAuthentication,
  ]);
}
